import { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ChannelContext } from '../../context';
import {
  ChannelRole,
  ChannelUsersFilters,
  organisationApi,
  OrganisationRole,
  OrganisationUserManagementStatus,
  OrganisationUserOrderBy,
  User,
} from '../../services';
import {
  Button,
  ButtonType,
  ConfirmationModal,
  getCssVar,
  IconLabel,
  ModalSlidePanel,
  OrderDirection,
  ORG_DASH_USERS,
  SearchField,
  useAppDispatch,
  useAppSelector,
  useDebounce,
  useIsNarrowViewport,
} from '../../shared';
import { getChannelUsers } from '../../slices';
import {
  AddUsersModal,
  UpdateUsersRoleModal,
  UsersColumns,
  UsersFilters,
  UsersTable,
} from '../Organisation';

import classNames from 'classnames';
import classes from './UserManagement.module.scss';

export interface GetChannelUsersArgs {
  page: number;
  query?: string;
  orderBy?: OrganisationUserOrderBy;
  orderDirection?: OrderDirection;
  filters?: ChannelUsersFilters;
}

export const UserManagement: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardUsers' });

  const dispatch = useAppDispatch();

  const isNarrowViewport = useIsNarrowViewport();

  const [organisationChannelUserRemoveFromChannel] =
    organisationApi.endpoints.organisationChannelUserRemoveFromChannel.useMutation();

  const {
    id: channelId,
    role: { id: roleId },
  } = useContext(ChannelContext).channel;

  // const [selectAll, setSelectAll] = useState<boolean>(false);

  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const [addUsersOpen, setAddUsersOpen] = useState<boolean>(false);

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  const [removeUsersOpen, setRemoveUsersOpen] = useState<boolean>(false);

  const [updateUsersRoleOpen, setUpdateUsersRoleOpen] = useState<boolean>(false);

  const [removeLoading, setRemoveLoading] = useState<boolean>(false);

  const [search, setSearch] = useState<string>('');

  const debouncedSearch = useDebounce<string>(search);

  const [cookies] = useCookies([ORG_DASH_USERS]);

  const requestFilters = useMemo(() => {
    const { channelsId, ...filters } = cookies[ORG_DASH_USERS]?.requestFilters ?? {};
    return filters;
  }, [cookies]);

  const appliedFiltersCount = useMemo(() => Object.keys(requestFilters).length, [requestFilters]);

  const selectedUserIds = useMemo(() => selectedUsers.map(({ id }) => id), [selectedUsers]);

  const selectedUsersLength = useMemo(() => selectedUsers.length, [selectedUsers.length]);

  const allowToManage = useMemo(
    () =>
      [
        ChannelRole.ADMIN,
        OrganisationRole.ADMIN,
        OrganisationRole.OWNER,
        OrganisationRole.TEAM_MEMBER,
      ].includes(roleId),
    [roleId]
  );

  const [orderByActive, setOrderByActive] = useState<OrganisationUserOrderBy>(
    OrganisationUserOrderBy.AUTHORIZED_TIME
  );

  const [orderDirectionActive, setOrderDirectionActive] = useState<OrderDirection>(
    OrderDirection.DESC
  );

  const { isFetched, users, pageInfo } = useAppSelector(({ channelUsers }) => channelUsers);

  const getUsers = useCallback(
    ({
      page,
      query = search,
      orderBy = orderByActive,
      orderDirection = orderDirectionActive,
      filters = requestFilters,
    }: GetChannelUsersArgs) => {
      dispatch(
        getChannelUsers({
          channelId,
          page,
          orderBy,
          orderDirection,
          filters: { query, ...filters },
          fetchManagementFields: true,
        })
      );
    },
    [channelId, dispatch, orderByActive, orderDirectionActive, requestFilters, search]
  );

  useEffect(
    () => getUsers({ page: 1 }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearch, orderByActive, orderDirectionActive]
  );

  useEffect(
    () => {
      // setSelectAll(false);
      setSelectedUsers([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearch]
  );

  const bulkActionsComponent = useMemo(() => {
    return (
      <>
        <IconLabel
          iconId={'edit'}
          iconSize={18}
          label={ctxT('update-role')}
          color={getCssVar('--base-link-text-color')}
          hoverColor={getCssVar('--base-link-text-hover-color')}
          onClick={() => setUpdateUsersRoleOpen(true)}
        />

        <IconLabel
          iconId={'delete'}
          iconSize={18}
          label={t('common.delete')}
          color={getCssVar('--color-danger')}
          hoverColor={getCssVar('--color-danger-hover')}
          onClick={() => setRemoveUsersOpen(true)}
        />
      </>
    );
  }, [ctxT, t]);

  const refresh = useCallback(() => {
    getUsers({ page: pageInfo.page });
    setSelectedUsers([]);
  }, [getUsers, pageInfo.page]);

  const removeUsersHandler = useCallback(async () => {
    setRemoveLoading(true);

    try {
      const userManagementResult = await organisationChannelUserRemoveFromChannel({
        channelId,
        userIds: selectedUserIds,
      }).unwrap();

      if (userManagementResult.length) {
        const hasSkip = userManagementResult.find(
          ({ status }) => status === OrganisationUserManagementStatus.SKIP
        );

        hasSkip ? toast.info(ctxT('remove-skip')) : toast.success(ctxT('remove-success'));

        refresh();
        return;
      }

      toast.error(t('common.error-message'));
    } catch (_) {
      toast.error(t('common.error-message'));
    } finally {
      setRemoveUsersOpen(false);
      setRemoveLoading(false);
    }
  }, [channelId, ctxT, organisationChannelUserRemoveFromChannel, refresh, selectedUserIds, t]);

  return (
    <div className={classes['user-management']}>
      <h1 className={classes['user-management__title']}>{t('adminLayoutMenu.user-management')}</h1>
      <div className={classes['user-management__intro']}>{t('userManagement.intro')}</div>
      <div
        className={classNames(classes['user-management__header'], {
          [classes['user-management__header--narrow']]: isNarrowViewport,
        })}
      >
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder={t('common.type-to-search')}
          className={classes['user-management__header-search']}
        />
        <div className={classes['user-management__header-buttons']}>
          <UsersColumns />

          <IconLabel
            iconId={'filter'}
            iconSize={18}
            label={`${t('common.filter')} (${appliedFiltersCount})`}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
            onClick={() => setFiltersOpen(true)}
          />

          {allowToManage && (
            <Button
              type={ButtonType.primary}
              label={ctxT('add-users')}
              onClick={() => setAddUsersOpen(true)}
            />
          )}
        </div>
      </div>

      <UsersTable
        isFetched={isFetched}
        users={users}
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
        orderByActive={orderByActive}
        setOrderByActive={setOrderByActive}
        orderDirectionActive={orderDirectionActive}
        setOrderDirectionActive={setOrderDirectionActive}
        getUsers={getUsers}
        pageInfo={pageInfo}
        bulkActionsComponent={bulkActionsComponent}
      />

      <AddUsersModal
        isOpen={addUsersOpen}
        setIsOpen={setAddUsersOpen}
        callback={refresh}
        channelId={channelId}
      />

      <UpdateUsersRoleModal
        isOpen={updateUsersRoleOpen}
        setIsOpen={setUpdateUsersRoleOpen}
        userIds={selectedUserIds}
        callback={refresh}
        channelId={channelId}
      />

      <ConfirmationModal
        isOpen={removeUsersOpen}
        onClose={() => setRemoveUsersOpen(false)}
        acceptLabel={t('common.delete')}
        onAccept={removeUsersHandler}
        title={ctxT('remove-users', { count: selectedUsersLength })}
        subTitle={ctxT('remove-users-sub-title')}
        loading={removeLoading}
        danger
      />

      <ModalSlidePanel
        isOpen={filtersOpen}
        body={
          <UsersFilters
            getUsers={getUsers}
            totalItems={pageInfo.totalItems}
            channelId={channelId}
          />
        }
        onClose={() => setFiltersOpen(false)}
        ariaLabel={t('common.filter')}
      />
    </div>
  );
};
